import { Address } from '@src/main/modules/messages/domain/address/address.interface';
import { Subject } from '@src/main/modules/messages/domain/subject/subject.interface';
import {
  FormattedInboxMessage,
  FormattedMessage,
  FormattedOutboxMessage,
} from '@src/main/modules/messages/ui/mailbox/model/formated-message.model';
import { Fleet } from '@src/main/modules/parameters/domain/fleet-positions/fleet.interface';
import { Position } from '@src/main/modules/parameters/domain/fleet-positions/position.interface';
import {
  BriefCommercialEvaluation,
  BriefFlightEvaluation,
} from '@src/main/modules/reports/shared/domain/evaluation.interface';
import {
  PositionAssignmentReport,
  PunctualityDetail,
} from '@src/main/modules/reports/shared/domain/report.interface';
import { Role } from '@src/main/modules/roles/domain/role.interface';
import { UserTableRow } from '@src/main/modules/user/ui/user-table-row.model';
import { formatDateCustom, formatDateTime } from '@src/main/shared/utils/dateUtils';
import { formatDate } from 'date-fns';
import { TFunction } from 'i18next';

export const normalizeInboxMessage = (msg: FormattedInboxMessage, t: TFunction) => ({
  ...normalizeCommonMessageFields(msg, t),
  [t('messages.mailbox.readDate')]: msg.readDate
    ? formatDate(msg.readDate, 'dd/MM/yyyy HH:mm')
    : '',
  [t('messages.mailbox.isResponded')]: msg.isResponded ? t('common.yes') : t('common.no'),
});

export const normalizeOutboxMessage = (msg: FormattedOutboxMessage, t: TFunction) => ({
  ...normalizeCommonMessageFields(msg, t),
  [t('messages.mailbox.outbox.initDate')]: msg.initDate
    ? formatDate(msg.initDate, 'dd/MM/yyyy HH:mm')
    : '',
  [t('messages.mailbox.outbox.endDate')]: msg.endDate
    ? formatDate(msg.endDate, 'dd/MM/yyyy HH:mm')
    : '',
});

const normalizeCommonMessageFields = (msg: FormattedMessage, t: TFunction) => ({
  [t('messages.mailbox.date')]: msg.sendDate ? formatDate(msg.sendDate, 'dd/MM/yyyy HH:mm') : '',
  [t('messages.mailbox.subject')]: msg.subject,
  [t('messages.mailbox.sentBy')]: msg.sendBy,
  [t('messages.mailbox.sentByName')]: msg.sendByName,
  [t('messages.mailbox.message')]: msg.message,
  [t('messages.mailbox.messageHtml')]: msg.messageHtml,
  [t('messages.mailbox.recipients')]: msg.recipients.join(', '),
  [t('messages.mailbox.attachment')]: msg.attachment,
});

export const normalizeSubject = (subject: Subject, t: TFunction) => ({
  [t('messages.subjects.description')]: subject.description,
  [t('messages.subjects.visibleOnApp')]: subject.visibleOnApp ? t('common.yes') : t('common.no'),
  [t('messages.subjects.addresses')]: subject.addresses
    .map((addr) => `${addr.description} <${addr.email}>`)
    .join(', '),
});

export const normalizeAddress = (address: Address, t: TFunction) => ({
  [t('messages.addresses.description')]: address.description,
  [t('messages.addresses.email')]: address.email,
});

export const normalizeUser = (user: UserTableRow, t: TFunction) => ({
  [t('usersManagement.employeeId')]: user.employeeId,
  [t('usersManagement.name')]: user.fullName,
  [t('usersManagement.roles')]: user.rolesDesc ?? '',
});

export const normalizeRole = (role: Role, t: TFunction) => ({
  [t('rolesManagement.role')]: role.role,
  [t('rolesManagement.description')]: role.description,
});

export const normalizeFleet = (fleet: Fleet, t: TFunction) => ({
  [t('parameters.fleetsAndPositions.fleets.fleet')]: fleet.fleet,
  [t('parameters.fleetsAndPositions.fleets.description')]: fleet.description,
});

export const normalizePosition = (position: Position, t: TFunction) => ({
  [t('parameters.fleetsAndPositions.positions.position')]: position.position,
  [t('parameters.fleetsAndPositions.positions.description')]: position.description,
});

export const normalizePunctualityReport = (report: PunctualityDetail, itemTitles: string[]) => {
  const baseData: Record<string, string> = {
    fechaVuelo: report.flightDate ? formatDateCustom(report.flightDate) : '',
    operador: report.carrierCode ?? '',
    numeroVuelo: report.flightNumber ?? '',
    origen: report.departureAirport ?? '',
    destino: report.arrivalAirport ?? '',
    matricula: report.aircraftRegistration ?? '',
    nominaSobrecargo: report.employeeId ?? '',
    nombreSobrecargo: report.employeeName ?? '',
    fechaRecepcion: report.updatedAt ? formatDateTime(report.updatedAt) : '',
    tiempoAvionListo: report.timePlaneReady ?? '',
    tipoFormulario: report.formType ?? '',
  };

  const itemsData: Record<string, string> = itemTitles.reduce(
    (acc, title) => {
      const matchingItem = report.items.find((item) => item.title === title);
      acc[title] = matchingItem ? matchingItem.time : '';
      return acc;
    },
    {} as Record<string, string>,
  );

  const paxData = (report.passengersKeys ?? []).reduce(
    (acc, pax) => {
      const key = pax.key ?? 'N/A';
      acc[`Pasajeros ${key}`] = pax.value ?? '';
      return acc;
    },
    {} as Record<string, string>,
  );

  const specialSituationsData = (report.specialSituations ?? []).reduce(
    (acc, sit) => {
      const key = sit.key ?? 'N/A';
      acc[key] = sit.value ?? '';
      return acc;
    },
    {} as Record<string, string>,
  );

  return {
    ...baseData,
    ...itemsData,
    ...paxData,
    ...specialSituationsData,
    pdf: `${window.location.origin}/forms/${report.formId}/pdf`,
  };
};

export const normalizeCommercialEvaluationReport = (evaluation: BriefCommercialEvaluation) => ({
  fechaVuelo: evaluation.flightDate ? formatDateCustom(evaluation.flightDate) : '',
  operador: evaluation.carrierCode ?? '',
  numeroVuelo: evaluation.flightNumber ?? '',
  origen: evaluation.departureAirport ?? '',
  destino: evaluation.arrivalAirport ?? '',
  matricula: evaluation.aircraftRegistration ?? '',
  nominaSupervisor: evaluation.supervisorEmployeeId ?? '',
  nominaEvaluado: evaluation.evaluatedEmployeeId ?? '',
  nombreEvaluado: [evaluation.evaluatedEmployeeName, evaluation.evaluatedEmployeeLastName]
    .filter(Boolean)
    .join(' '),
  fechaRecepcion: evaluation.receptionDate ? formatDateTime(evaluation.receptionDate) : '',
  pdf: `${window.location.origin}/commercial-evaluations/${evaluation.id}/pdf`,
});

export const normalizeFlightEvaluationReport = (evaluation: BriefFlightEvaluation) => ({
  fechaVuelo: evaluation.flightDate ? formatDateCustom(evaluation.flightDate) : '',
  operador: evaluation.carrierCode ?? '',
  numeroVuelo: evaluation.flightNumber ?? '',
  origen: evaluation.departureAirport ?? '',
  destino: evaluation.arrivalAirport ?? '',
  matricula: evaluation.aircraftRegistration ?? '',
  nominaSobrecargo: evaluation.purserEmployeeId ?? '',
  nombreSobrecargo: evaluation.purserEmployeeName ?? '',
  fechaRecepcion: evaluation.receptionDate ? formatDateTime(evaluation.receptionDate) : '',
  pdf: `${window.location.origin}/flight-evaluations/${evaluation.id}/pdf`,
});

export const normalizePositionAssignmentReport = (
  evaluation: PositionAssignmentReport,
): Record<string, string> => ({
  fechaVuelo: evaluation.flightDate ? formatDateCustom(evaluation.flightDate) : '',
  numeroVuelo: evaluation.flightNumber ?? '',
  origen: evaluation.departureAirport ?? '',
  destino: evaluation.arrivalAirport ?? '',
  matricula: evaluation.aircraftRegistration ?? '',
});
